import { Box, Button, Flex, Grid, Text } from '@chakra-ui/react';

import { EditIcon2, SaveIcon } from '@assets/icons';
import { useEffect, useState } from 'react';
import { usePromotionClaim, initialState2 } from '@pages/ConsumerPromotion/hooks/state/usePromotionClaim';
import { detailItem, detailItemTag } from '@pages/ConsumerPromotion/utils/components';
import EditingForm from './EditingForm';
import { dateFormatter, validateObjectFields } from '@helpers/utils';
import useMutateCPList from '@pages/ConsumerPromotion/hooks/mutate/useMutateCPList';
import { keys, get } from 'lodash';
import { STATUS } from '@pages/ConsumerPromotion/utils/const';
import SkeletonComponent from '@components/skeleton';
import { ConditionalComponent } from '@components/functionComponent';

export default function EditOverview({ data, isLoading, isCanEdit = true }) {
    const state = usePromotionClaim();
    const [invalidFields, setInvalidFields] = useState({});
    const { updateCP } = useMutateCPList();

    const getKeys = keys(initialState2);
    const excludeProperty = ['set', 'reset', 'comment', 'created_by_name', ...getKeys];
    const disabledEdit = ['reject', 'active', 'waiting for approval', 'ready to claim', 'done', 'processing'].includes(
        data?.promotion_status?.status?.toLowerCase(),
    );

    const handleClickEdit = () => {
        state.set({ is_edit: true });
    };

    const handleSaveEdit = () => {
        const { invalids, hasInvalid } = validateObjectFields(state, excludeProperty);
        setInvalidFields(invalids);

        if (hasInvalid) return;

        const payload = {
            promotion_name: state.promotion_name,
            scheme_id: state.promotion_scheme.id,
            start_date: dateFormatter(state.start_date, 'YYYY-MM-DD'),
            end_date: dateFormatter(state.end_date, 'YYYY-MM-DD'),
            tfm: state.tfm_id,
        };

        updateCP.mutate({ id: data?.id, payload });
        state.set({ is_edit: false });
    };

    const variant = STATUS[data?.promotion_status.status?.toLowerCase?.()] || 'draft';

    const fieldConfigs = [
        {
            label: 'Status',
            key: 'promotion_status.status',
            fallback: 'Draft',
            variant: variant,
            type: 'tag',
        },
        { type: 'spacer' },
        ...(data?.comment != null ? [{ label: 'Comment', key: 'comment', fallback: '-' }, { type: 'spacer' }] : []),
        { label: 'Promotion Name', key: 'promotion_name', fallback: '-' },
        { type: 'spacer' },
        { label: 'Promo Scheme', key: 'promotion_scheme.scheme', format: (v) => `${v}%`, fallback: '-' },
        { label: 'No. PM', key: 'tfm_id', fallback: '-' },
        { label: 'Start Date', key: 'start_date', format: dateFormatter, fallback: '-' },
        { label: 'End Date', key: 'end_date', format: dateFormatter, fallback: '-' },
        {
            label: 'SKU Associated',
            key: 'total_sku',
            format: (list) => (list === 0 ? 'Belum di isi' : list),
            isError: (list) => list === 0,
        },
        {
            label: 'Customers',
            key: 'total_customer',
            format: (list) => (list === 0 ? 'Belum di isi' : list),
            isError: (list) => list === 0,
        },
        { label: 'Created by', key: 'created_by_name', fallback: '-' },
        { label: 'Created Date', key: 'created_at', format: dateFormatter, fallback: '-' },
    ];

    const fields = fieldConfigs.map((cfg) => {
        if (cfg.type === 'spacer') return { type: 'spacer' };

        if (isLoading) {
            return {
                ...cfg,
                value: <SkeletonComponent width={'100px'} height={cfg.label === 'Status' ? '18px' : '24px'} />,
                isError: false,
            };
        }

        const rawValue = get(data, cfg.key);
        const value = rawValue != null ? (cfg.format ? cfg.format(rawValue) : rawValue) : (cfg.fallback ?? '-');

        return {
            ...cfg,
            value,
            isError: cfg.isError ? cfg.isError(rawValue) : false,
        };
    });

    useEffect(() => {
        if (data) {
            const mappedData = {
                ...data,
                promotion_scheme: {
                    id: data.promotion_scheme.id,
                    name: `${data.promotion_scheme.scheme}%`,
                },
            };
            state.set(mappedData);
        }
    }, [data]);

    return (
        <Box>
            <Flex justifyContent={'space-between'} mb={'24px'}>
                <Text fontWeight={'600'} fontSize={'sm'}>
                    Overview
                </Text>

                <ConditionalComponent statement={isCanEdit && !disabledEdit}>
                    {!state.is_edit ? (
                        <Button
                            variant={'unstyled'}
                            color={'sagitarius.500'}
                            fontWeight={'700'}
                            onClick={handleClickEdit}
                            disabled={disabledEdit}
                        >
                            <EditIcon2 /> Edit
                        </Button>
                    ) : (
                        <Flex gap={'12px'}>
                            <Button variant={'outline'} onClick={() => state.set({ is_edit: false })}>
                                Batal
                            </Button>
                            <Button variant={'outline'} onClick={handleSaveEdit}>
                                <SaveIcon size={20} /> Simpan
                            </Button>
                        </Flex>
                    )}
                </ConditionalComponent>
            </Flex>

            <Box>
                {state.is_edit ? (
                    <EditingForm data={data} fields={fields} invalidFields={invalidFields} />
                ) : (
                    <Grid templateColumns="repeat(2, 1fr)" gap={'16px'} gridColumnGap={'56px'}>
                        {fields.map((f, idx) => {
                            if (f.type === 'spacer') {
                                return <Box key={idx} />;
                            }

                            if (f.type === 'tag') {
                                return detailItemTag(f, idx);
                            }

                            return detailItem(f, idx);
                        })}
                    </Grid>
                )}
            </Box>
        </Box>
    );
}
